import React, { useState } from 'react';
import { Chip, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FilterBuilder } from './FilterBuilder';
import { countFindFilterDimensions, FindFilterValue } from './types';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../design-system';

interface FiltersProps {
  filter: FindFilterValue | null;
  onAdd?: (options: Partial<FindFilterValue>) => void;
  onChange: (filter: FindFilterValue) => void;
  onRemove: () => void;
  enableSiteFilter?: boolean;
  enableImageFilter?: boolean;
  enableAmcrIdFilter?: boolean;
  enableGroupFilter?: boolean;
  enablePreset?: boolean;
  onLoadPreset?: () => void;
  onSavePreset?: () => void;
  showRemoveButton?: boolean;
}

const Filters = ({
  filter,
  onAdd,
  onChange,
  onRemove,
  enableSiteFilter = true,
  enableImageFilter = false,
  enableAmcrIdFilter = false,
  enableGroupFilter = true,
  enablePreset = true,
  onLoadPreset,
  onSavePreset,
  showRemoveButton = true,
}: FiltersProps) => {
  const [showEraFilter, setShowEraFilter] = useState(!!filter?.eras);
  const [showSiteFilter, setShowSiteFilter] = useState(!!filter?.sites);
  const [showFindTypeFilter, setShowFindTypeFilter] = useState(
    !!filter?.findTypes
  );
  const [showMaterialFilter, setShowMaterialFilter] = useState(
    !!filter?.materials
  );
  const [showGroupFilter, setShowGroupFilter] = useState(!!filter?.groups);
  const [showTagFilter, setShowTagFilter] = useState(!!filter?.tags);
  const [showImageFilter, setShowImageFilter] = useState(
    filter?.hasImages != null
  );
  const [showAmcrIdFilter, setShowAmcrIdFilter] = useState(
    filter?.hasAmcrId != null
  );

  const handleChange = (updatedFilter: FindFilterValue) => {
    if (countFindFilterDimensions(updatedFilter) === 0) {
      if (filter) {
        onRemove();
      }

      return;
    }

    if (!filter && onAdd) {
      onAdd(updatedFilter);
      return;
    }

    onChange(updatedFilter);
  };

  const handleAddEraFilter = () => {
    setShowEraFilter(true);
  };

  const handleAddSiteFilter = () => {
    setShowSiteFilter(true);
  };

  const handleAddFindTypeFilter = () => {
    setShowFindTypeFilter(true);
  };

  const handleAddMaterialFilter = () => {
    setShowMaterialFilter(true);
  };

  const handleAddGroupFilter = () => {
    setShowGroupFilter(true);
  };

  const handleAddTagFilter = () => {
    setShowTagFilter(true);
  };

  const handleAddImageFilter = () => {
    setShowImageFilter(true);
  };

  const handleAddAmcrIdFilter = () => {
    setShowAmcrIdFilter(true);
  };

  const handleRemove = () => {
    setShowEraFilter(false);
    setShowSiteFilter(false);
    setShowFindTypeFilter(false);
    setShowMaterialFilter(false);
    setShowGroupFilter(false);
    setShowTagFilter(false);
    setShowImageFilter(false);
    setShowAmcrIdFilter(false);
    onRemove();
  };

  const eraVisible = showEraFilter || !!filter?.eras?.length;
  const siteVisible = showSiteFilter || !!filter?.sites?.length;
  const findTypeVisible = showFindTypeFilter || !!filter?.findTypes?.length;
  const materialVisible = showMaterialFilter || !!filter?.materials?.length;
  const groupVisible = showGroupFilter || !!filter?.groups?.length;
  const tagVisible = showTagFilter || !!filter?.tags?.length;
  const imageVisible = showImageFilter || filter?.hasImages != null;
  const amcrIdVisible = showAmcrIdFilter || filter?.hasAmcrId != null;

  const anyRowShown =
    eraVisible ||
    siteVisible ||
    findTypeVisible ||
    materialVisible ||
    groupVisible ||
    tagVisible ||
    imageVisible ||
    amcrIdVisible;

  return (
    <>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {!eraVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.eras"
                defaultMessage="Filter eras"
              />
            }
            variant="outlined"
            onClick={handleAddEraFilter}
          />
        )}
        {enableSiteFilter && !siteVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.sites"
                defaultMessage="Filter sites"
              />
            }
            variant="outlined"
            onClick={handleAddSiteFilter}
          />
        )}
        {!findTypeVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.findTypes"
                defaultMessage="Filter find types"
              />
            }
            variant="outlined"
            onClick={handleAddFindTypeFilter}
          />
        )}
        {!materialVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.materials"
                defaultMessage="Filter materials"
              />
            }
            variant="outlined"
            onClick={handleAddMaterialFilter}
          />
        )}
        {enableGroupFilter && !groupVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.groups"
                defaultMessage="Filter groups"
              />
            }
            variant="outlined"
            onClick={handleAddGroupFilter}
          />
        )}
        {!tagVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.tags"
                defaultMessage="Filter tags"
              />
            }
            variant="outlined"
            onClick={handleAddTagFilter}
          />
        )}
        {enableImageFilter && !imageVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.images"
                defaultMessage="Filter images"
              />
            }
            variant="outlined"
            onClick={handleAddImageFilter}
          />
        )}
        {enableAmcrIdFilter && !amcrIdVisible && (
          <Chip
            icon={<AddIcon />}
            size="small"
            label={
              <FormattedMessage
                id="filter.add.amcrId"
                defaultMessage="Filter PAS ID"
              />
            }
            variant="outlined"
            onClick={handleAddAmcrIdFilter}
          />
        )}
        {enablePreset && onLoadPreset && (
          <Button
            density="compact"
            tone="neutral"
            variant="outlined"
            onClick={onLoadPreset}
          >
            <FormattedMessage
              id="filter.preset.load"
              defaultMessage="Load preset"
            />
          </Button>
        )}
        {enablePreset && onSavePreset && (
          <Button
            density="compact"
            tone="neutral"
            variant="outlined"
            onClick={onSavePreset}
          >
            <FormattedMessage
              id="filter.preset.save"
              defaultMessage="Save preset"
            />
          </Button>
        )}
      </Stack>
      {anyRowShown && (
        <FilterBuilder
          filter={filter}
          onChange={handleChange}
          onRemove={handleRemove}
          showEraFilter={eraVisible}
          showSiteFilter={enableSiteFilter && siteVisible}
          showFindTypeFilter={findTypeVisible}
          showMaterialFilter={materialVisible}
          showGroupFilter={enableGroupFilter && groupVisible}
          showTagFilter={tagVisible}
          showImageFilter={enableImageFilter && imageVisible}
          showAmcrIdFilter={enableAmcrIdFilter && amcrIdVisible}
          showRemoveButton={showRemoveButton}
        />
      )}
    </>
  );
};

export { Filters };
export * from './types';
export type { FiltersProps };
